"use client";
import {useEffect, useRef, useState} from "react";
import type {PDFDocumentProxy, RenderTask} from "pdfjs-dist";
export default function PortfolioPdfViewer({url}:{url:string}) {
  const [document,setDocument]=useState<PDFDocumentProxy|null>(null);
  const [error,setError]=useState("");
  const [zoom,setZoom]=useState(1);
  const [width,setWidth]=useState(600);
  const [rendered,setRendered]=useState(false);
  const viewport=useRef<HTMLDivElement>(null);
  const pages=useRef<HTMLDivElement>(null);
  useEffect(()=>{
    let cancelled=false;
    let destroy:(()=>void)|undefined;
    void import("pdfjs-dist").then(pdfjs=>{
      if(cancelled)return;
      pdfjs.GlobalWorkerOptions.workerSrc="/pdf.worker.min.mjs";
      const task=pdfjs.getDocument({url,enableXfa:false,useSystemFonts:true});
      destroy=()=>{void task.destroy();};
      return task.promise.then(doc=>{if(!cancelled)setDocument(doc);});
    }).catch(()=>{if(!cancelled)setError("The PDF could not be loaded. You can still download the document above.");});
    return()=>{cancelled=true;destroy?.();};
  },[url]);
  useEffect(()=>{
    if(!viewport.current)return;
    const observer=new ResizeObserver(([entry])=>{if(entry.contentRect.width>160)setWidth(entry.contentRect.width-32);});
    observer.observe(viewport.current);
    return()=>observer.disconnect();
  },[]);
  useEffect(()=>{
    if(!document||!pages.current)return;
    let cancelled=false;
    const tasks:RenderTask[]=[];
    const host=pages.current;
    host.replaceChildren();setRendered(false);
    void(async()=>{
      for(let number=1;number<=document.numPages;number++){
        const page=await document.getPage(number);if(cancelled)return;
        const natural=page.getViewport({scale:1});
        const scale=(width/natural.width)*zoom;
        const view=page.getViewport({scale});
        const density=Math.min(window.devicePixelRatio||1,2);
        const section=window.document.createElement("section");section.className="pdf-page";
        section.setAttribute("aria-label",`Résumé page ${number}`);
        const canvas=window.document.createElement("canvas");
        canvas.width=Math.ceil(view.width*density);canvas.height=Math.ceil(view.height*density);
        canvas.style.width=`${view.width}px`;canvas.style.height=`${view.height}px`;
        canvas.setAttribute("role","img");canvas.setAttribute("aria-label",`Page ${number} of ${document.numPages}`);
        section.appendChild(canvas);host.appendChild(section);
        const task=page.render({canvas,canvasContext:canvas.getContext("2d")!,viewport:view,transform:density===1?undefined:[density,0,0,density,0,0]});
        tasks.push(task);await task.promise;if(cancelled)return;
        const text=await page.getTextContent();if(cancelled)return;
        const accessible=window.document.createElement("p");accessible.className="pdf-accessible-text";
        accessible.textContent=text.items.map(item=>"str" in item?item.str:"").join(" ");section.appendChild(accessible);
      }
      if(!cancelled)setRendered(true);
    })().catch(err=>{if(!cancelled&&err?.name!=="RenderingCancelledException")setError("The PDF could not be rendered. Download the document to read it.");});
    return()=>{cancelled=true;tasks.forEach(task=>task.cancel());};
  },[document,width,zoom]);
  return <div className="pdf-renderer">
    <div className="pdf-controls"><span>{document?`${document.numPages} page${document.numPages===1?"":"s"}`:"Loading PDF…"}</span><button aria-label="Zoom out PDF" disabled={zoom<=.6} onClick={()=>setZoom(value=>Math.max(.6,value-.2))}>−</button><button aria-label="Fit PDF to window" onClick={()=>setZoom(1)}>{Math.round(zoom*100)}%</button><button aria-label="Zoom in PDF" disabled={zoom>=1.8} onClick={()=>setZoom(value=>Math.min(1.8,value+.2))}>+</button></div>
    <div className="pdf-pages-scroll" ref={viewport}>
      {error?<p role="alert" className="pdf-error">{error}</p>:!rendered&&<p role="status" className="pdf-loading">Rendering document…</p>}
      <div ref={pages} className="pdf-pages" />
    </div>
  </div>;
}
